var GiftWithPurchase = class extends HTMLElement {
  constructor() {
    super();
    this.gifts = JSON.parse(this.querySelector('[data-gifts-json]').innerHTML)?.filter(gift => gift.available)
    this.cartTotal = 0
    this.idsInCart = []
    this._onCartChangedListener = this._onCartChanged.bind(this)
    this.getCart()
  }

  async getCart () {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartData = cartJson
    this.cartItems = cartJson.items

    this.updateCartVariables()
    this.checkGifts()
    this.removeStaleGifts()
  }

  async connectedCallback() {
    this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
    document.addEventListener("cart:change", this._onCartChangedListener);
    // Listen for the explicit removal of any gift with purchase.
    document.addEventListener('click', this.removeAndIgnoreGiftListener);
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
    document.removeEventListener('click', this.removeAndIgnoreGiftListener);
  }

  /**
   * Handles the event of a gift being removed from the cart.
   * @param {Event} event
   */
  removeAndIgnoreGiftListener = (event) => {
    const giftVariantId = event.target.getAttribute('data-gift-variant-id');
    if (!giftVariantId || !event.target.closest('line-item-quantity')) {
      // Not a gift being removed.
      return;
    }
    this.ignoreGift(giftVariantId);
  }

  /**
   * Marks a gift as ignored by storing its identifier in session storage.
   * @param {string} variantId - The gift's variant identifier to be ignored.
   */
  ignoreGift = (variantId) => {
    sessionStorage.setItem(`gwp-${variantId}`, ''); // No value needed.
  }

  /**
   * Checks if a gift is marked as ignored in session storage.
   * @param {string} variantId - The gift's variant identifier to check.
   * @returns {boolean} - Returns `true` if the gfit is ignored, otherwise `false`.
   */
  giftIsIgnored = (variantId) => {
    return sessionStorage.getItem(`gwp-${variantId}`) !== null;
  }

  /**
   * Removes the ignored status of a gift by removing it from session storage.
   * @param {string} variantId - The gift's variant identifier to allow.
   */
  allowGift = (variantId) => {
    sessionStorage.removeItem(`gwp-${variantId}`);
  }

  _onCartChanged(event) {
    this.cartItems = event.detail.cart.items
    this.updateCartVariables()
    this.checkGifts(event.detail.baseEvent);
    this.removeStaleGifts()
  }

  /**
   * Manages the gift variants in the cart based on promo requirements and specific cart events.
   * @async
   * @param {string} eventType - The type of event triggering the gift check, e.g. `variant:add`.
   */
  async checkGifts(eventType) {
    let reload = false
    let modify = false
    const atcData = { items: [] }
    const changeData = { updates: {} }
    // A gift stops being ignored if its qualifying product is attached with one of the following events.
    const targetEventTypes = ['variant:add'];

    const giftsByVariantId = new Map();
    
    this.gifts.forEach(gift => {
      const variantId = gift.id;
      const hasValidRequirements = this.checkGiftRequirementMet(gift);
      
      if (!giftsByVariantId.has(variantId)) {
        giftsByVariantId.set(variantId, {
          variant_id: variantId,
          type: gift.type,
          requirement: gift.requirement,
          promotions: [],
          has_any_valid_requirements: false,
          has_new_subscriber_promotion: false
        });
      }
      
      const giftGroup = giftsByVariantId.get(variantId);
      giftGroup.promotions.push({
        type: gift.type,
        requirement: gift.requirement,
        has_valid_requirements: hasValidRequirements
      });
      
      if (hasValidRequirements) {
        giftGroup.has_any_valid_requirements = true;
      }
      
      if (gift.type === 'new_subscriber') {
        giftGroup.has_new_subscriber_promotion = true;
        if (hasValidRequirements) {
          giftGroup.type = 'new_subscriber';
        }
      }
    });

    const giftVariants = Array.from(giftsByVariantId.values());

    // Lookahead to see whether a `new_subscriber` gift should be added.
    const newSubscriberGift = this.gifts.find(gift =>
      gift.type === 'new_subscriber' && this.checkGiftRequirementMet(gift)
    );

    for (const giftVariant of giftVariants) {
      if (giftVariant.has_any_valid_requirements && targetEventTypes.includes(eventType)) {
        // Adding a qualifying product to your cart will always allow the gift.
        this.allowGift(giftVariant.variant_id);
      }

      if (this.giftIsIgnored(giftVariant.variant_id)) {
        continue;
      }

      const giftInCart = this.getGiftInCart(giftVariant.variant_id);

      // Skip other gifts if a `new_subscriber` gift is valid.
      if (newSubscriberGift && !giftVariant.has_new_subscriber_promotion) {
        // Remove a previous gift because the `new_subscriber` gift takes priority.
        if (giftInCart) {
          changeData.updates[giftInCart.key] = 0;
          modify = true;
        }
        continue;
      }

      // Process the current gift based on its validity and presence in the cart.
      if (giftVariant.has_any_valid_requirements && !giftInCart) {
        atcData.items.push({
          id: giftVariant.variant_id,
          quantity: 1,
          properties: {
            '_gift-with-purchase': true
          },
        });
      } else if (!giftVariant.has_any_valid_requirements && giftInCart) {
        changeData.updates[giftInCart.key] = 0;
        modify = true;
      } else if (giftVariant.has_any_valid_requirements && giftInCart && giftInCart.quantity > 1) {
        changeData.updates[giftInCart.key] = 1;
        modify = true;
      }
    }

    // Add new gifts to the cart.
    if (atcData.items.length) {
      await this.addToCart(atcData);
      reload = true;
    }

    // Modify the cart to remove stale or conflicting gifts.
    if (modify) {
      await this.modifyGiftInCart(changeData);
      reload = true;
    }

    if (window.themeVariables.settings.pageType !== 'cart' || !reload) {
      return;
    }

    window.location.reload();
  }

  // Remove GWP items when associated GWP metaobject is deleted / disabled
  async removeStaleGifts() {
    const changeData = { updates: {} }
    for (const item of this.cartItems) {
      if (Object.hasOwn(item.properties, '_gift-with-purchase') && !this.gifts.some(gift => gift.id === item.variant_id)) {
        changeData.updates[item.key] = 0
      }
    }

    if (Object.keys(changeData.updates).length) {
      await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(changeData)
      })
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  checkGiftRequirementMet (gift) {
    if (gift.type === 'Cart Total') {
      return this.cartTotal >= gift.requirement

    } else if (gift.type === 'Item in Cart') {
      return gift.requirement.split(',').some(id => this.idsInCart.includes(Number(id)))

    } else if (gift.type === 'new_subscriber') {
      // If the user is logged in and tagged as such.
      const isSubscriber = gift.requirement.includes('ordergroove-subscriber') ||
        gift.requirement.includes('Active Ordergroove Subscriber');
      // If the cart has any subscription products.
      const cartQualifies = this.cartItems.some((item) => item.selling_plan_allocation);
      return !isSubscriber && cartQualifies;

    } else if (!gift.type) {
      return false
    }

    return false
  }

  async addToCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(data),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }
  }

  async modifyGiftInCart (data) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    })
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    }
  }

  getGiftInCart (giftVariantId) {
    return this.cartItems.find(item => item.id === giftVariantId && Object.hasOwn(item.properties, '_gift-with-purchase'))
  }

  updateCartVariables () {
    this.cartTotal = this.cartItems.reduce((total, item) => {
      if (Object.hasOwn(item.properties, '_gift-with-purchase')) return total
      return total + item.final_line_price
    }, 0)

    this.idsInCart = this.cartItems.filter((item) => item.properties['_gift-with-purchase'] !== true).map(item => item.id)
  }
};
if (!window.customElements.get('gift-with-purchase')) {
  window.customElements.define('gift-with-purchase', GiftWithPurchase);
}

var LineItemVariantSelector = class extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select')
    this.key = this.dataset.key
    this.value = this.select.value
    this.getCart()
  }

  async getCart() {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartItems = cartJson.items
    this.init()
  }

  init () {
    this.cartItem = this.cartItems.find(item => item.key === this.key)
    this.select.addEventListener('change', this.changeVariant.bind(this))
  }

  async changeVariant () {
    if (this.select.value === this.value) { return }
    const changeData = {
      id: this.cartItem.key,
      quantity: 0
    }

    const addData = {
      items: [
        {
          id: this.select.value,
          quantity: this.cartItem.quantity,
          ...(this.cartItem.selling_plan_allocation?.selling_plan?.id) && { selling_plan: this.cartItem.selling_plan_allocation.selling_plan.id }
        }
      ]
    }

    // Remove Old Item
    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(changeData)
    })

    // Add New Item
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: JSON.stringify(addData),
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        "X-Requested-With": "XMLHttpRequest"
      }
    })
    const responseJson = await response.json()
    if (response.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }))
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
    }

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-variant-selector')) {
  window.customElements.define('line-item-variant-selector', LineItemVariantSelector);
}


var LineItemSellingPlanSelector = class extends HTMLElement {
  constructor() {
    super();
    this.select = this.querySelector('select')
    this.key = this.dataset.key
    this.value = this.select.value
    this.getCart()
  }

  async getCart() {
    const cartJson = await (await fetch(`${Shopify.routes.root}cart.js`)).json()
    this.cartItems = cartJson.items
    this.init()
  }

  init() {
    this.cartItem = this.cartItems.find(item => item.key === this.key)
    this.select.addEventListener('change', this.changeSellingPlan.bind(this))
  }

  async changeSellingPlan() {
    if (this.select.value === this.value) { return }
    const changeData = {
      id: this.cartItem.key,
      selling_plan: (this.select.value === '0' ? null : Number(this.select.value)),
      quantity: this.cartItem.quantity
    }

    await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(changeData)
    })

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true
    }))

    if (window.themeVariables.settings.pageType !== "cart") { return }
    window.location.reload()
  }
};
if (!window.customElements.get('line-item-selling-plan-selector')) {
  window.customElements.define('line-item-selling-plan-selector', LineItemSellingPlanSelector);
}

export {
  GiftWithPurchase
}